import React, { useState, useCallback, useMemo } from 'react';
import { 
    Typography, 
    Box, 
    TextField, 
    Button, 
    MenuItem, 
    Collapse, 
    IconButton, 
    InputAdornment,
    Card,
    CardContent,
    Grid,
    Chip,
    Divider,
    Alert,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { 
    FaEye, 
    FaEyeSlash, 
    FaUser, 
    FaLock, 
    FaEnvelope, 
    FaPhone, 
    FaCalendarAlt, 
    FaCreditCard, 
    FaBuilding,
    FaClock,
    FaSave,
    FaShieldAlt
} from 'react-icons/fa';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';

const AccountSettings = () => {
    const location = useLocation();
    const { user } = location.state || {};
    
    // Fallback para user do localStorage se não estiver no state
    const userFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
    const currentUser = user || userFromStorage;

    // Debug logs para verificar dados do usuário
    console.log('=== ACCOUNT SETTINGS DEBUG ===');
    console.log('User from location.state:', user);
    console.log('User from localStorage:', userFromStorage);
    console.log('Current user:', currentUser);
    console.log('IBAN from currentUser:', currentUser?.IBAN);
    console.log('=== END DEBUG ===');

    // Componente otimizado sem animações

    const [username] = useState(currentUser?.username || '');
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [nomeCompleto, setNomeCompleto] = useState(currentUser?.nome_completo || '');
    const [estadoCivil, setEstadoCivil] = useState(currentUser?.estado_civil || 'Solteiro');
    const [contacto, setContacto] = useState(currentUser?.contacto || '');
    const [email, setEmail] = useState(currentUser?.email || '');
    const [contactoEmergencia, setContactoEmergencia] = useState(currentUser?.contacto_emergencia || '');
    const [IBAN, setIBAN] = useState(currentUser?.IBAN || '');
    const [dataNascimento, setDataNascimento] = useState(currentUser?.data_nascimento || '');
    const [showPasswordFields, setShowPasswordFields] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorDetails, setErrorDetails] = useState(null);

    const apiBaseURL = 'https://plasticemployers.duckdns.org';

    const togglePasswordFields = useCallback(() => {
        setShowPasswordFields(prev => !prev);
    }, []);

    const handleIBANChange = useCallback((event) => {
        const newValue = event.target.value;
        let value = newValue.replace(/\s/g, '');
        value = value.replace(/(.{4})/g, '$1 ').trim();
        setIBAN(value);
    }, []);

    const handleNomeCompletoChange = useCallback((event) => {
        setNomeCompleto(event.target.value);
    }, []);

    const handleEstadoCivilChange = useCallback((event) => {
        setEstadoCivil(event.target.value);
    }, []);

    const handleContactoChange = useCallback((event) => {
        setContacto(event.target.value);
    }, []);

    const handleEmailChange = useCallback((event) => {
        setEmail(event.target.value);
    }, []);

    const handleContactoEmergenciaChange = useCallback((event) => {
        setContactoEmergencia(event.target.value);
    }, []);

    const handleDataNascimentoChange = useCallback((event) => {
        setDataNascimento(event.target.value);
    }, []);

    const handleOldPasswordChange = useCallback((event) => {
        setOldPassword(event.target.value);
    }, []);

    const handleNewPasswordChange = useCallback((event) => {
        setNewPassword(event.target.value);
    }, []);

    const handleConfirmPasswordChange = useCallback((event) => {
        setConfirmPassword(event.target.value);
    }, []);

    const toggleShowOldPassword = useCallback(() => {
        setShowOldPassword(prev => !prev);
    }, []);

    const toggleShowNewPassword = useCallback(() => {
        setShowNewPassword(prev => !prev);
    }, []);

    const toggleShowConfirmPassword = useCallback(() => {
        setShowConfirmPassword(prev => !prev);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);
        setErrorModalOpen(false);
        setErrorDetails(null);

        // Validar IBAN - deve ter exatamente 25 dígitos (sem espaços)
        const ibanDigits = IBAN.replace(/\s/g, '');
        if (ibanDigits && ibanDigits.length !== 25) {
            setSubmitStatus({ 
                type: 'error', 
                message: `O IBAN deve ter exatamente 25 dígitos. Atual: ${ibanDigits.length} dígitos.` 
            });
            setIsSubmitting(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            setSubmitStatus({ type: 'error', message: 'As novas senhas não coincidem!' });
            setIsSubmitting(false);
            return;
        }

        try {
            const updatedUser = {
                username,
                old_password: oldPassword,
                new_password: newPassword || undefined,
                nome_completo: nomeCompleto,
                estado_civil: estadoCivil,
                contacto,
                email,
                contacto_emergencia: contactoEmergencia,
                IBAN,
                data_nascimento: dataNascimento ? dataNascimento : null,
            };

            const token = localStorage.getItem('access');
            await axios.put(`${apiBaseURL}/api/me/`, updatedUser, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setSubmitStatus({ type: 'success', message: 'Dados atualizados com sucesso!' });
            
            // Atualizar localStorage com os novos dados
            const updatedUserData = { ...currentUser, ...updatedUser };
            localStorage.setItem('user', JSON.stringify(updatedUserData));
            console.log('Updated localStorage with:', updatedUserData);
            
            // Limpar campos de senha
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowPasswordFields(false);
        } catch (error) {
            console.error('Erro ao atualizar os dados:', error);
            console.log('Error response:', error.response);
            console.log('Error response data:', error.response?.data);
            console.log('Error response status:', error.response?.status);
            
            // Capturar erros específicos do backend
            if (error.response && error.response.data) {
                const backendErrors = error.response.data;
                console.log('Backend errors to display:', backendErrors);
                setErrorDetails(backendErrors);
                setErrorModalOpen(true);
                setSubmitStatus({ type: 'error', message: 'Erro de validação. Verifique os detalhes.' });
            } else {
                setSubmitStatus({ type: 'error', message: 'Ocorreu um erro ao atualizar os dados. Por favor, tente novamente.' });
            }
        }
        setIsSubmitting(false);
    };

    const InfoCard = useMemo(() => ({ title, icon, children, color = '#667eea' }) => (
        <Card sx={{ 
            background: `linear-gradient(135deg, ${color}10, ${color}05)`,
            border: `1px solid ${color}30`,
            borderRadius: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 25px ${color}20`,
            }
        }}>
            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        backgroundColor: `${color}20`,
                        color: color,
                        mr: 2
                    }}>
                        {icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        {title}
                    </Typography>
                </Box>
                {children}
            </CardContent>
        </Card>
    ), []);

    // Verificar se temos dados do usuário
    if (!currentUser || !currentUser.username) {
        return (
            <DashboardLayout>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: 'error.main' }}>
                        <FaUser size={40} />
                    </Avatar>
                    <Typography variant="h4" gutterBottom sx={{ color: 'error.main', fontWeight: 'bold' }}>
                        Dados do usuário não encontrados
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Por favor, faça login novamente para acessar as configurações da conta.
                    </Typography>
                </Box>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <Box>
                    {/* Header */}
                    <Box sx={{ mb: 4 }}>
                        <Typography 
                            variant="h3" 
                            sx={{ 
                                fontWeight: 'bold',
                                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                mb: 1
                            }}
                        >
                            Configurações da Conta
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                            Gerencie suas informações pessoais e de segurança
                        </Typography>
                    </Box>

                    {/* Status Alert */}
                    {submitStatus && (
                        <Alert 
                            severity={submitStatus.type} 
                            sx={{ mb: 3, borderRadius: 2 }}
                            onClose={() => setSubmitStatus(null)}
                        >
                            {submitStatus.message}
                        </Alert>
                    )}

                    <form>
                        <Grid container spacing={3}>
                            {/* Informações Pessoais */}
                            <Grid item xs={12} md={6}>
                                <InfoCard title="Informações Pessoais" icon={<FaUser />} color="#4ecdc4">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Username"
                                                fullWidth
                                                value={username}
                                                InputProps={{ readOnly: true }}
                                                sx={{ 
                                                    '& .MuiInputBase-root': { 
                                                        backgroundColor: 'rgba(0,0,0,0.02)' 
                                                    } 
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Nome Completo"
                                                fullWidth
                                                value={nomeCompleto}
                                                onChange={handleNomeCompletoChange}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                select
                                                label="Estado Civil"
                                                fullWidth
                                                value={estadoCivil}
                                                onChange={handleEstadoCivilChange}
                                            >
                                                <MenuItem value="Solteiro">Solteiro</MenuItem>
                                                <MenuItem value="Casado">Casado</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Data de Nascimento"
                                                fullWidth
                                                value={dataNascimento}
                                                onChange={handleDataNascimentoChange}
                                                type="date"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                    </Grid>
                                </InfoCard>
                            </Grid>

                            {/* Informações de Contato */}
                            <Grid item xs={12} md={6}>
                                <InfoCard title="Informações de Contato" icon={<FaPhone />} color="#ff6b6b">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Contacto"
                                                fullWidth
                                                value={contacto}
                                                onChange={handleContactoChange}
                                                required
                                                inputProps={{ maxLength: 9 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Email"
                                                fullWidth
                                                value={email}
                                                onChange={handleEmailChange}
                                                required
                                                type="email"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Contacto de Emergência"
                                                fullWidth
                                                value={contactoEmergencia}
                                                onChange={handleContactoEmergenciaChange}
                                                required
                                                inputProps={{ maxLength: 9 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="IBAN"
                                                fullWidth
                                                value={IBAN}
                                                onChange={handleIBANChange}
                                                placeholder="PT50 0002 0123 1234 5678 9015 4"
                                                required
                                                helperText={`${IBAN.replace(/\s/g, '').length}/25 dígitos`}
                                                error={IBAN.replace(/\s/g, '').length > 0 && IBAN.replace(/\s/g, '').length !== 25}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Box sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center',
                                                                color: IBAN.replace(/\s/g, '').length === 25 ? 'success.main' : 'text.secondary'
                                                            }}>
                                                                {IBAN.replace(/\s/g, '').length === 25 ? '✓' : '⚠'}
                                                            </Box>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </InfoCard>
                            </Grid>

                            {/* Segurança */}
                            <Grid item xs={12}>
                                <InfoCard title="Segurança da Conta" icon={<FaShieldAlt />} color="#fdcb6e">
                                    <Box sx={{ mb: 2 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={togglePasswordFields}
                                            startIcon={showPasswordFields ? <FaEyeSlash /> : <FaEye />}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            {showPasswordFields ? 'Ocultar Alteração de Senha' : 'Alterar Senha'}
                                        </Button>
                                    </Box>
                                    
                                    <Collapse in={showPasswordFields} timeout="auto" unmountOnExit>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    label="Senha Atual"
                                                    type={showOldPassword ? 'text' : 'password'}
                                                    fullWidth
                                                    value={oldPassword}
                                                    onChange={handleOldPasswordChange}
                                                    required={showPasswordFields}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={toggleShowOldPassword}
                                                                    edge="end"
                                                                >
                                                                    {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    label="Nova Senha"
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    fullWidth
                                                    value={newPassword}
                                                    onChange={handleNewPasswordChange}
                                                    required={showPasswordFields}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={toggleShowNewPassword}
                                                                    edge="end"
                                                                >
                                                                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    label="Confirmar Nova Senha"
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    fullWidth
                                                    value={confirmPassword}
                                                    onChange={handleConfirmPasswordChange}
                                                    required={showPasswordFields}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={toggleShowConfirmPassword}
                                                                    edge="end"
                                                                >
                                                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Collapse>
                                </InfoCard>
                            </Grid>

                            {/* Informações da Empresa */}
                            <Grid item xs={12}>
                                <InfoCard title="Informações da Empresa" icon={<FaBuilding />} color="#98d8c8">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                label="Data de Contrato"
                                                fullWidth
                                                value={currentUser?.data_contrato || ''}
                                                InputProps={{ readOnly: true }}
                                                sx={{ 
                                                    '& .MuiInputBase-root': { 
                                                        backgroundColor: 'rgba(0,0,0,0.02)' 
                                                    } 
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                label="Turno"
                                                fullWidth
                                                value={currentUser?.turno || ''}
                                                InputProps={{ readOnly: true }}
                                                sx={{ 
                                                    '& .MuiInputBase-root': { 
                                                        backgroundColor: 'rgba(0,0,0,0.02)' 
                                                    } 
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                label="Seção"
                                                fullWidth
                                                value={currentUser?.seccao || ''}
                                                InputProps={{ readOnly: true }}
                                                sx={{ 
                                                    '& .MuiInputBase-root': { 
                                                        backgroundColor: 'rgba(0,0,0,0.02)' 
                                                    } 
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                                <Chip 
                                                    label="Funcionário Ativo" 
                                                    color="success" 
                                                    variant="outlined"
                                                    icon={<FaClock />}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </InfoCard>
                            </Grid>
                        </Grid>

                        {/* Botão de Salvar */}
                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                            <Button 
                                onClick={handleSubmit}
                                variant="contained" 
                                size="large"
                                disabled={isSubmitting || (IBAN.replace(/\s/g, '').length > 0 && IBAN.replace(/\s/g, '').length !== 25)}
                                startIcon={<FaSave />}
                                sx={{ 
                                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                                    }
                                }}
                            >
                                {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
                            </Button>
                        </Box>
                    </form>
                </Box>

                {/* Modal de Erro */}
                <Dialog 
                    open={errorModalOpen} 
                    onClose={() => setErrorModalOpen(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{ 
                        color: 'error.main', 
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <FaShieldAlt />
                        Erro de Validação
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Os seguintes campos têm problemas de validação:
                        </Typography>
                        
                        {/* Debug info */}
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: 'grey.600' }}>
                                Debug: {JSON.stringify(errorDetails)}
                            </Typography>
                        </Box>
                        
                        {errorDetails && typeof errorDetails === 'object' && Object.entries(errorDetails).map(([field, errors]) => (
                            <Box key={field} sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.dark', mb: 1 }}>
                                    {field.replace(/_/g, ' ').toUpperCase()}:
                                </Typography>
                                {Array.isArray(errors) ? errors.map((error, index) => (
                                    <Typography key={index} variant="body2" sx={{ color: 'error.dark' }}>
                                        • {error}
                                    </Typography>
                                )) : (
                                    <Typography variant="body2" sx={{ color: 'error.dark' }}>
                                        • {errors}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                        
                        {!errorDetails && (
                            <Typography variant="body2" sx={{ color: 'error.dark' }}>
                                Nenhum detalhe de erro disponível.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            onClick={() => setErrorModalOpen(false)}
                            variant="contained"
                            sx={{ 
                                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                                '&:hover': {
                                    background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
                                }
                            }}
                        >
                            Fechar
                        </Button>
                    </DialogActions>
                </Dialog>
        </DashboardLayout>
    );
};

export default AccountSettings;
